import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react'
import { authService, AuthUser, AuthError } from '../services/auth'
import { isAuthConfigured } from '../config/msal'
import { api } from '../services/api'
import { getEnv } from '../config/env'

const DEV_BYPASS_AUTH = getEnv('VITE_DEV_BYPASS_AUTH') === 'true'

const DEV_MOCK_USER: AuthUser = {
  id: 'dev-user-id',
  name: 'Dev Admin',
  email: 'dev@localhost',
  roles: ['Admin']
}

interface AuthContextValue {
  isAuthenticated: boolean
  isLoading: boolean
  user: AuthUser | null
  error: AuthError | null
  login: () => Promise<void>
  logout: () => Promise<void>
  getAccessToken: () => Promise<string | null>
}

const AuthContext = createContext<AuthContextValue>({
  isAuthenticated: false,
  isLoading: true,
  user: null,
  error: null,
  login: async () => {},
  logout: async () => {},
  getAccessToken: async () => null
})

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

interface AuthProviderProps {
  children: ReactNode
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [isLoading, setIsLoading] = useState(true)
  const [user, setUser] = useState<AuthUser | null>(null)
  const [error, setError] = useState<AuthError | null>(null)

  useEffect(() => {
    const init = async () => {
      // Dev bypass mode for local development without Azure AD
      if (DEV_BYPASS_AUTH && import.meta.env.DEV) {
        console.info('DEV_BYPASS_AUTH enabled - using mock admin user')
        setUser(DEV_MOCK_USER)
        // No token needed in bypass mode - API also bypasses
        api.setTokenProvider(async () => 'dev-bypass-token')
        setIsLoading(false)
        return
      }

      // Set up token provider for API client
      api.setTokenProvider(async () => {
        try {
          return await authService.getAccessToken()
        } catch {
          return null
        }
      })

      // Check for error in URL params
      const urlParams = new URLSearchParams(window.location.search)
      const authError = authService.parseAuthError(urlParams)

      if (authError) {
        setError(authError)
        // Clean URL
        window.history.replaceState({}, '', window.location.pathname)
        setIsLoading(false)
        return
      }

      // Skip auth if not configured
      if (!isAuthConfigured()) {
        console.warn('Azure AD not configured. Admin features disabled.')
        setIsLoading(false)
        return
      }

      try {
        await authService.initialize()
        const currentUser = authService.getUser()
        setUser(currentUser)
      } catch (err) {
        console.error('Auth initialization error:', err)
        setError({
          code: 'init_error',
          message: 'Failed to initialize authentication'
        })
      } finally {
        setIsLoading(false)
      }
    }

    init()
  }, [])

  const login = useCallback(async () => {
    setError(null)
    try {
      await authService.login()
    } catch (err) {
      setError({
        code: 'login_error',
        message: (err as Error).message || 'Login failed'
      })
    }
  }, [])

  const logout = useCallback(async () => {
    try {
      await authService.logout()
    } catch (err) {
      console.error('Logout error:', err)
    }
  }, [])

  const getAccessToken = useCallback(async () => {
    try {
      return await authService.getAccessToken()
    } catch (err) {
      console.error('Token acquisition error:', err)
      return null
    }
  }, [])

  const value: AuthContextValue = {
    isAuthenticated: !!user,
    isLoading,
    user,
    error,
    login,
    logout,
    getAccessToken
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}

export { AuthContext }
